/*!
 * DropStudios Drop 01 v1.0.0 | Theme scripts
 * © 2026 DropStudios, a brand of Ma'at Studios LLC. All rights reserved.
 * Proprietary code. Copying, scraping, redistributing or using it to train AI models is prohibited without written permission.
 * https://www.maatstudios.co
 */

/* Dependency-free custom elements, prefixed ds-. Store data comes from window.DropStudios (layout/theme.liquid). */

const theme = window.DropStudios || {};

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || theme.strings?.cartError);
  return data;
};

const htmlFrom = (markup, selector) => new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);

/* Focus rings show only while the visitor navigates with the keyboard. */
document.addEventListener('keydown', (event) => {
  if (event.key === 'Tab') document.documentElement.classList.add('keyboard-nav');
});
document.addEventListener('pointerdown', () => document.documentElement.classList.remove('keyboard-nav'));

/** Keeps focus inside `container` while it is open; returns a release function. */
const trapFocus = (container) => {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKeydown);
  return () => container.removeEventListener('keydown', onKeydown);
};

/** Base class for drawers (menu, cart): open/close, Escape, overlay, focus trap, scroll lock. */
class DrawerElement extends HTMLElement {
  open(trigger) {
    this.trigger = trigger || document.activeElement;
    this.drawer.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.release = trapFocus(this.drawer);
    this.onKeyup = (event) => event.key === 'Escape' && this.close();
    document.addEventListener('keyup', this.onKeyup);
    requestAnimationFrame(() => (this.drawer.querySelector('[tabindex="-1"], button, a') || this.drawer).focus());
    this.trigger?.setAttribute?.('aria-expanded', 'true');
  }

  close() {
    this.drawer.hidden = true;
    document.documentElement.style.overflow = '';
    this.release?.();
    document.removeEventListener('keyup', this.onKeyup);
    this.trigger?.setAttribute?.('aria-expanded', 'false');
    this.trigger?.focus?.();
  }
}

class CartDrawer extends DrawerElement {
  connectedCallback() {
    this.drawer = this;
    this.addEventListener('click', (event) => event.target.closest('[data-close]') && this.close());
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (!toggle || theme.cartType !== 'drawer') return;
      event.preventDefault();
      this.open(toggle);
    });
  }

  renderContents(sections) {
    const markup = sections?.['cart-drawer'];
    if (!markup) return;
    const next = htmlFrom(markup, '#CartDrawerInner');
    if (next) this.querySelector('#CartDrawerInner').replaceWith(next);
  }
}
customElements.define('ds-cart-drawer', CartDrawer);

const updateCartCount = (count) => {
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.classList.toggle('is-empty', count === 0);
  });
};

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('[data-error]');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    // Buy now: a cart permalink sends this variant and quantity straight to checkout.
    if (this.hasAttribute('data-buy-now')) {
      event.preventDefault();
      const data = new FormData(this.form);
      window.location.href = `${theme.routes.cart}/${data.get('id')}:${data.get('quantity') || 1}`;
      return;
    }
    const drawer = document.querySelector('ds-cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    if (this.button.getAttribute('aria-busy') === 'true') return;
    this.button.setAttribute('aria-busy', 'true');
    this.error.hidden = true;
    const body = Object.fromEntries(new FormData(this.form));
    body.sections = 'cart-drawer';
    body.sections_url = window.location.pathname;
    try {
      const response = await fetchJSON(`${theme.routes.cartAdd}.js`, { method: 'POST', body: JSON.stringify(body) });
      drawer.renderContents(response.sections);
      const cart = await fetchJSON(`${theme.routes.cart}.js`);
      updateCartCount(cart.item_count);
      drawer.open(this.button);
    } catch (error) {
      this.error.textContent = error.message;
      this.error.hidden = false;
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('ds-product-form', ProductForm);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.addEventListener('change', () => this.onChange());
    this.markAvailability();
  }

  selectedOptions() {
    return [...this.querySelectorAll('fieldset')].map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const select = fieldset.querySelector('select');
      return checked ? checked.value : select?.value;
    });
  }

  markAvailability() {
    const selected = this.selectedOptions();
    this.querySelectorAll('fieldset').forEach((fieldset, index) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const options = [...selected];
        options[index] = input.value;
        const match = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  onChange() {
    const options = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.querySelectorAll('fieldset').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-selected-value]');
      if (label) label.textContent = options[i];
    });
    this.markAvailability();
    const form = document.getElementById(this.dataset.form);
    const button = form?.querySelector('[type="submit"]');
    const label = form?.querySelector('[data-add-label]');
    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = theme.strings.unavailable;
      return;
    }
    const idInput = form?.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;
    if (button) button.disabled = !variant.available;
    const prices = JSON.parse(form?.querySelector('[data-variant-prices]')?.textContent || 'null');
    const base = label?.dataset.label || theme.strings.addToCart;
    const addLabel = prices ? `${base} — ${prices[variant.id]}` : base;
    if (label) label.textContent = variant.available ? addLabel : theme.strings.soldOut;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    this.refreshSection(url);
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  async refreshSection(url) {
    const section = this.dataset.section;
    const response = await fetch(`${url.pathname}?variant=${url.searchParams.get('variant')}&section_id=${section}`);
    const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
    document.querySelectorAll(`[data-refresh][id$="-${section}"]`).forEach((element) => {
      const fresh = markup.getElementById(element.id);
      if (fresh) element.replaceWith(fresh);
    });
  }
}
customElements.define('ds-variant-picker', VariantPicker);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const step = button.name === 'plus' ? 1 : -1;
      const min = Number(this.input.min || 0);
      this.input.value = Math.max(min, Number(this.input.value || 0) + step);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('ds-quantity-input', QuantityInput);

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const stepper = event.target.closest('ds-quantity-input[data-line]');
      if (!stepper) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(Number(stepper.dataset.line), Number(event.target.value)), 300);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (remove) this.update(Number(remove.dataset.removeLine), 0);
    });
  }

  async update(line, quantity) {
    this.classList.add('is-loading');
    const inDrawer = !!this.closest('ds-cart-drawer');
    const section = inDrawer ? 'cart-drawer' : this.dataset.section;
    try {
      const cart = await fetchJSON(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({ line, quantity, sections: section, sections_url: window.location.pathname }),
      });
      updateCartCount(cart.item_count);
      if (inDrawer) document.querySelector('ds-cart-drawer')?.renderContents(cart.sections);
      else if (section && cart.sections?.[section]) {
        // Cart page: swap in the re-rendered section instead of reloading.
        const next = htmlFrom(cart.sections[section], `#shopify-section-${section}`);
        const current = document.getElementById(`shopify-section-${section}`);
        if (next && current) current.replaceChildren(...next.childNodes);
        else window.location.reload();
      } else window.location.reload();
    } catch (error) {
      window.alert(error.message);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('ds-cart-items', CartItems);

class StickyHeader extends HTMLElement {
  connectedCallback() {
    const hideOnScroll = this.dataset.sticky === 'scroll-up';
    const transparent = this.hasAttribute('data-transparent');
    if (!hideOnScroll && !transparent) return;
    const staysTransparent = this.dataset.scrolledStyle === 'transparent';
    const first = document.querySelector('#MainContent > .shopify-section');
    let last = window.scrollY;
    const measure = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    measure();
    window.addEventListener('resize', measure);
    // How much of the header is on screen, for bars that stick right under it.
    const measureVisible = () => document.documentElement.style.setProperty('--header-visible', `${Math.max(0, Math.round(this.getBoundingClientRect().bottom))}px`);
    this.addEventListener('transitionend', measureVisible);
    const update = () => {
      measureVisible();
      const y = window.scrollY;
      // A see-through bar switches to the page text color once it leaves the first section.
      const scrolled = staysTransparent && first ? first.getBoundingClientRect().bottom <= this.offsetHeight : y > 10;
      if (transparent) this.classList.toggle('is-scrolled', scrolled);
      if (hideOnScroll) this.classList.toggle('is-hidden', y > last && y > this.offsetHeight * 2);
      last = y;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }
}
customElements.define('ds-sticky-header', StickyHeader);


/* Infinite marquee: converts the speed setting (px/s on a 1920px canvas) into a loop duration. */
class ProductMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-marquee__track');
    this.list = this.track.querySelector('.product-marquee__list');
    this.resize = () => this.fill();
    this.fill();
    window.addEventListener('resize', this.resize);
    this.list.querySelectorAll('img').forEach((img) => img.complete || img.addEventListener('load', this.resize, { once: true }));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resize);
  }

  /* Repeats the product list so each half of the track is wider than the screen: the loop never shows a gap. */
  fill() {
    const width = this.list.offsetWidth;
    if (!width) return;
    const copies = Math.max(1, Math.ceil(this.offsetWidth / width)) * 2;
    const lists = this.track.querySelectorAll('.product-marquee__list');
    if (lists.length !== copies) {
      lists.forEach((list) => list !== this.list && list.remove());
      for (let i = 1; i < copies; i++) {
        const clone = this.list.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        this.track.append(clone);
      }
    }
    this.updateDuration();
  }

  updateDuration() {
    const loop = this.track.scrollWidth / 2;
    const speed = Number(this.dataset.speed || 60) * Math.max(window.innerWidth / 1920, 0.35);
    if (loop > 0) this.style.setProperty('--marquee-duration', `${(loop / speed).toFixed(2)}s`);
  }
}
customElements.define('ds-product-marquee', ProductMarquee);

/* Rows of videos and images scrolling forever; videos only play while they are on screen. */
/** Plays muted looping videos only while they are on screen. Returns the observer. */
const playWhenVisible = (videos) => {
  if (!videos.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting && target.offsetParent !== null) {
        target.muted = true;
        target.play().catch(() => {});
      } else target.pause();
    });
  });
  videos.forEach((video) => observer.observe(video));
  return observer;
};

class MediaTicker extends HTMLElement {
  connectedCallback() {
    this.rows = [...this.querySelectorAll('.media-ticker__row')];
    this.resize = () => this.rows.forEach((row) => this.fill(row));
    this.resize();
    window.addEventListener('resize', this.resize);
    this.querySelectorAll('img').forEach((img) => img.complete || img.addEventListener('load', this.resize, { once: true }));
    this.watchVideos();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resize);
    this.observer?.disconnect();
  }

  /* Repeats a row's list so each half of the track is wider than the screen: the loop never shows a gap. */
  fill(row) {
    const track = row.querySelector('.media-ticker__track');
    const list = track.querySelector('.media-ticker__list');
    const width = list.offsetWidth;
    if (!width) return;
    const copies = Math.max(1, Math.ceil(this.offsetWidth / width)) * 2;
    const lists = track.querySelectorAll('.media-ticker__list');
    if (lists.length !== copies) {
      lists.forEach((other) => other !== list && other.remove());
      for (let i = 1; i < copies; i++) {
        const clone = list.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        track.append(clone);
      }
      this.watchVideos();
    }
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const speed = mobile && this.dataset.speedMobile ? Number(this.dataset.speedMobile) : Number(this.dataset.speed || 40) * Math.max(window.innerWidth / 1920, 0.35);
    track.style.setProperty('--ticker-duration', `${(track.scrollWidth / 2 / speed).toFixed(2)}s`);
  }

  watchVideos() {
    this.observer?.disconnect();
    this.observer = playWhenVisible([...this.querySelectorAll('video')]);
  }
}
customElements.define('ds-media-ticker', MediaTicker);

/* Before / after comparison: the divider follows the pointer or finger, and sways on its own until touched. */
class BeforeAfter extends HTMLElement {
  connectedCallback() {
    this.hovering = false;
    const move = (clientX) => {
      const rect = this.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      this.style.setProperty('--exposure', `${(x / rect.width) * 100}%`);
    };
    this.addEventListener('mousemove', (event) => {
      this.hovering = true;
      move(event.clientX);
    });
    this.addEventListener('mouseleave', () => (this.hovering = false));
    this.addEventListener('touchmove', (event) => {
      this.hovering = true;
      move(event.touches[0].clientX);
    }, { passive: true });
    this.addEventListener('touchend', () => (this.hovering = false));
    if (this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.play() : this.stop()));
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stop();
  }

  play() {
    const tick = () => {
      if (!this.hovering) this.style.setProperty('--exposure', `${50 + Math.sin(Date.now() / 1500) * 30}%`);
      this.frame = requestAnimationFrame(tick);
    };
    this.stop();
    this.frame = requestAnimationFrame(tick);
  }

  stop() {
    cancelAnimationFrame(this.frame);
  }
}
customElements.define('ds-before-after', BeforeAfter);

/* Product gallery: swipe slider with dots on phones; on larger screens one image at a time with arrows. */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.product-gallery__slide')];
    this.markers = [...this.querySelectorAll('.product-gallery__dot, .product-gallery__thumb')];
    this.thumbs = this.querySelector('.product-gallery__thumbs');
    this.list = this.querySelector('.product-gallery__slides');
    this.status = this.querySelector('[data-status]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.desktop = window.matchMedia('(min-width: 750px)');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.setupLightbox();
    if (this.slides.length < 2) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.markers.forEach((marker) => marker.addEventListener('click', () => this.go(Number(marker.dataset.index))));
    this.addEventListener('keydown', (event) => {
      if (this.lightbox?.open) return;
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    this.list.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.desktop.addEventListener('change', () => this.go(this.index, false));
    this.closest('.shopify-section')?.addEventListener('variant:change', (event) => {
      const id = event.detail.variant?.featured_media?.id;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (target !== -1) this.go(target);
    });
    this.go(this.index, false);
  }

  go(index, announce = true) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      if (!active) slide.querySelector('video')?.pause();
    });
    this.mark(announce);
    if (!this.desktop.matches) {
      this.scrolling = true;
      this.list.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: announce ? 'smooth' : 'auto' });
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => (this.scrolling = false), 500);
    }
    if (announce && this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
  }

  /* Highlights the dot or thumbnail of the current image and keeps the thumbnail in view. */
  mark(smooth = true) {
    this.markers.forEach((marker) => marker.setAttribute('aria-current', String(Number(marker.dataset.index) === this.index)));
    const thumb = this.thumbs?.querySelector(`[data-index="${this.index}"]`);
    if (thumb && this.thumbs.scrollWidth > this.thumbs.clientWidth) {
      this.thumbs.scrollTo({ left: thumb.offsetLeft - (this.thumbs.clientWidth - thumb.clientWidth) / 2, behavior: smooth ? 'smooth' : 'auto' });
    }
  }

  onScroll() {
    if (this.desktop.matches || this.scrolling) return;
    const index = Math.round(this.list.scrollLeft / this.list.clientWidth);
    if (index === this.index) return;
    this.index = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.mark();
  }

  /* Full-screen viewer: opens on the current image, swipes or steps between images, closes on Escape or outside the image. */
  setupLightbox() {
    if (!this.lightbox) return;
    const track = this.lightbox.querySelector('[data-lightbox-slides]');
    const step = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    const open = () => {
      this.opener = document.activeElement;
      this.viewing = this.index;
      this.lightbox.showModal();
      track.scrollTo({ left: this.index * track.clientWidth, behavior: 'auto' });
    };
    track.addEventListener('scroll', () => {
      if (track.clientWidth) this.viewing = Math.round(track.scrollLeft / track.clientWidth);
    }, { passive: true });
    this.querySelector('[data-zoom]')?.addEventListener('click', open);
    this.list.addEventListener('click', (event) => {
      if (event.target.closest('img')) open();
    });
    this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
    this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => step(-1));
    this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => step(1));
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    });
    this.lightbox.addEventListener('click', (event) => {
      if (event.target.classList.contains('product-lightbox__slide') || event.target === track) this.lightbox.close();
    });
    this.lightbox.addEventListener('close', () => {
      if (this.slides.length > 1 && Number.isInteger(this.viewing) && this.viewing !== this.index) this.go(this.viewing, false);
      this.opener?.focus();
    });
  }
}
customElements.define('ds-product-gallery', ProductGallery);

/* Sticky buy bar: shows once the buy button is above the screen and mirrors the selected variant. */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    this.button = this.querySelector('[type="submit"]');
    this.options = this.querySelector('[data-sticky-options]');
    if (this.options) this.setupOptions();
    this.onVariantChange = (event) => {
      const { variant } = event.detail;
      this.markSelected(String(variant.id));
      this.button.disabled = !variant.available;
    };
    document.addEventListener('variant:change', this.onVariantChange);
    const target = this.form?.querySelector('[type="submit"]');
    if (!target) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', visible);
      if (!visible) this.close(false);
    });
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('pointerdown', this.onOutside);
  }

  /* Option menu: a button that opens a listbox, with arrow keys, Enter, Escape and click outside. */
  setupOptions() {
    this.toggle = this.options.querySelector('[data-sticky-toggle]');
    this.label = this.options.querySelector('[data-sticky-label]');
    this.list = this.options.querySelector('[data-sticky-list]');
    this.items = [...this.list.querySelectorAll('[role="option"]')];
    this.toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
    this.toggle.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      this.open();
    });
    this.items.forEach((item) => {
      item.addEventListener('click', () => this.choose(item));
      item.addEventListener('pointermove', () => this.activate(item));
    });
    this.list.addEventListener('keydown', (event) => this.onKeydown(event));
    this.onOutside = (event) => {
      if (this.isOpen() && !this.options.contains(event.target)) this.close(false);
    };
    document.addEventListener('pointerdown', this.onOutside);
  }

  isOpen() {
    return Boolean(this.options?.classList.contains('is-open'));
  }

  open() {
    this.options.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.activate(this.items.find((item) => item.getAttribute('aria-selected') === 'true') || this.items[0]);
    this.list.focus();
  }

  close(returnFocus = true) {
    if (!this.isOpen()) return;
    this.options.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.list.removeAttribute('aria-activedescendant');
    if (returnFocus) this.toggle.focus();
  }

  activate(item) {
    this.items.forEach((option) => option.classList.toggle('is-active', option === item));
    this.list.setAttribute('aria-activedescendant', item.id);
  }

  choose(item) {
    if (!item || item.getAttribute('aria-disabled') === 'true') return;
    this.close();
    if (item.getAttribute('aria-selected') !== 'true') this.selectVariant(item.dataset.value);
  }

  onKeydown(event) {
    const index = this.items.findIndex((item) => item.classList.contains('is-active'));
    const last = this.items.length - 1;
    const moves = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: last };
    if (event.key in moves) {
      event.preventDefault();
      this.activate(this.items[(moves[event.key] + this.items.length) % this.items.length]);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.choose(this.items[index]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.close(false);
    }
  }

  markSelected(id) {
    if (!this.items) return;
    this.items.forEach((item) => {
      const selected = item.dataset.value === id;
      item.setAttribute('aria-selected', String(selected));
      if (!selected) return;
      const name = item.querySelector('.sticky-buy__option-name').textContent;
      this.label.textContent = name;
      this.toggle.setAttribute('aria-label', `${this.toggle.dataset.name}: ${name}`);
    });
  }

  selectVariant(id) {
    const picker = document.querySelector(`ds-variant-picker[data-form="${this.dataset.form}"]`);
    const variant = picker?.variants.find((item) => String(item.id) === id);
    if (!variant) {
      const input = this.form?.querySelector('[data-variant-id]');
      if (input) input.value = id;
      this.markSelected(id);
      return;
    }
    picker.querySelectorAll('fieldset').forEach((fieldset, i) => {
      fieldset.querySelectorAll('input').forEach((input) => (input.checked = input.value === variant.options[i]));
      const select = fieldset.querySelector('select');
      if (select) select.value = variant.options[i];
    });
    picker.onChange();
  }
}
customElements.define('ds-sticky-buy', StickyBuy);

/* Feature tabs: switches a feature's media between its desktop and mobile versions. Phones start on mobile. */
class FeatureTab extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.show(tab.dataset.view));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const next = this.tabs[(this.tabs.indexOf(tab) + 1) % this.tabs.length];
        this.show(next.dataset.view);
        next.focus();
      });
    });
    if (this.tabs.length && window.matchMedia('(max-width: 749px)').matches) this.show('mobile');
    this.observer = playWhenVisible([...this.querySelectorAll('video')]);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  show(view) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.view === view;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.dataset.view !== view;
      const video = panel.querySelector('video');
      if (!video) return;
      if (panel.hidden) video.pause();
      else if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) video.play().catch(() => {});
    });
  }
}
customElements.define('ds-feature-tab', FeatureTab);

/* Reviews: a page at a time on larger screens, one swipeable row on phones, and the "Write a review" pop-up. */
class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.setupDialog();
    this.list = this.querySelector('[data-review-list]');
    if (!this.list) return;
    this.items = [...this.querySelectorAll('[data-review]')];
    this.nav = this.querySelector('[data-pages]');
    this.perPage = Number(this.dataset.perPage) || 10;
    this.pages = Math.ceil(this.items.length / this.perPage);
    this.phone = window.matchMedia('(max-width: 749px)');
    this.onResize = () => this.layout();
    this.phone.addEventListener('change', this.onResize);
    this.layout();
  }

  disconnectedCallback() {
    this.phone?.removeEventListener('change', this.onResize);
  }

  setupDialog() {
    const dialog = this.querySelector('[data-review-dialog]');
    if (!dialog) return;
    this.querySelector('[data-review-open]')?.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-review-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
    // Reopened after sending so the visitor sees the confirmation or the errors.
    if (this.querySelector('[data-review-sent]')) dialog.showModal();
  }

  layout() {
    if (this.phone.matches) {
      this.items.forEach((item) => (item.hidden = false));
      this.list.tabIndex = 0;
      if (this.nav) this.nav.hidden = true;
      return;
    }
    this.list.removeAttribute('tabindex');
    if (this.pages < 2 || !this.nav) return;
    this.nav.hidden = false;
    this.render(this.page || 1, false);
  }

  render(page, scroll = true) {
    this.page = Math.min(Math.max(page, 1), this.pages);
    this.items.forEach((item, i) => (item.hidden = Math.floor(i / this.perPage) + 1 !== this.page));
    const list = this.nav.querySelector('ul');
    list.replaceChildren();
    const add = (text, target, options = {}) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'reviews__page';
      button.textContent = text;
      button.setAttribute('aria-label', options.label);
      if (options.current) button.setAttribute('aria-current', 'page');
      button.disabled = Boolean(options.disabled);
      button.addEventListener('click', () => this.render(target));
      item.append(button);
      list.append(item);
    };
    const { previous, next, page: pageLabel } = this.nav.dataset;
    add('‹', this.page - 1, { label: previous, disabled: this.page === 1 });
    for (let i = 1; i <= this.pages; i++) add(String(i), i, { label: pageLabel.replace('[page]', i), current: i === this.page });
    add('›', this.page + 1, { label: next, disabled: this.page === this.pages });
    if (scroll) this.querySelector('[data-review-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
customElements.define('ds-product-reviews', ProductReviews);

/* Full-screen collection panels: videos only play while their panel is on screen. */
class CollectionStack extends HTMLElement {
  connectedCallback() {
    const videos = [...this.querySelectorAll('video')];
    if (!videos.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => video.pause());
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && video.offsetParent !== null) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.1 });
    videos.forEach((video) => {
      video.pause();
      observer.observe(video);
    });
  }
}
customElements.define('ds-collection-stack', CollectionStack);

/* Password page: password form in a dialog (reopened after a wrong password). */
class PasswordPage extends HTMLElement {
  connectedCallback() {
    const dialog = this.querySelector('[data-password-dialog]');
    if (!dialog) return;
    this.querySelector('[data-password-open]')?.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-password-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
    if (this.querySelector('[data-password-error]')) dialog.showModal();
  }
}
customElements.define('ds-password-page', PasswordPage);

/* Countdown to a launch date and time in the store's time zone; hides itself once it has passed. */
class LaunchCountdown extends HTMLElement {
  connectedCallback() {
    const offset = (this.dataset.offset || '+0000').replace(/(\d{2})(\d{2})$/, '$1:$2');
    this.target = Date.parse(`${this.dataset.date}T${this.dataset.time}:00${offset}`);
    if (Number.isNaN(this.target)) return;
    this.numbers = Object.fromEntries([...this.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el]));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, this.target - Date.now());
    this.hidden = left === 0;
    if (!left) return clearInterval(this.timer);
    const parts = { days: left / 864e5, hours: (left / 36e5) % 24, minutes: (left / 6e4) % 60, seconds: (left / 1e3) % 60 };
    for (const [unit, value] of Object.entries(parts)) {
      if (this.numbers[unit]) this.numbers[unit].textContent = String(Math.floor(value)).padStart(2, '0');
    }
    const days = this.querySelector('[data-days-wrap]');
    if (days) days.hidden = parts.days < 1;
  }
}
customElements.define('ds-launch-countdown', LaunchCountdown);

/* Announcement bar: messages in an endless row. Also tells the see-through header how much of the bar is on screen. */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.announcement__track');
    this.list = this.querySelector('.announcement__list');
    this.animate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onResize = () => {
      this.fill();
      this.measure();
    };
    this.onScroll = () => this.measure();
    this.fill();
    this.measure();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    document.documentElement.style.removeProperty('--announcement-visible');
  }

  measure() {
    document.documentElement.style.setProperty('--announcement-visible', `${Math.max(0, Math.round(this.getBoundingClientRect().bottom))}px`);
  }

  /* Repeats the messages so each half of the track is wider than the screen, then sets the loop duration from the speed. */
  fill() {
    const width = this.list.offsetWidth;
    if (!this.animate || !width) return;
    const copies = Math.max(1, Math.ceil(this.offsetWidth / width)) * 2;
    const lists = this.track.querySelectorAll('.announcement__list');
    if (lists.length !== copies) {
      lists.forEach((list) => list !== this.list && list.remove());
      for (let i = 1; i < copies; i++) {
        const clone = this.list.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        this.track.append(clone);
      }
    }
    const speed = Number(this.dataset.speed || 50) * Math.max(window.innerWidth / 1920, 0.35);
    this.style.setProperty('--marquee-duration', `${(this.track.scrollWidth / 2 / speed).toFixed(2)}s`);
  }
}
customElements.define('ds-announcement-bar', AnnouncementBar);

/* Side panel product list: a swipe carousel on phones, with progress bar and optional auto-slide. */
class ScrollingProducts extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.scrolling-products__list');
    this.bar = this.querySelector('.scrolling-products__progress-bar');
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.list.addEventListener('scroll', () => this.updateProgress(), { passive: true });
    window.addEventListener('resize', () => this.updateProgress());
    this.updateProgress();

    const delay = Number(this.dataset.autoplay) * 1000;
    if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    ['pointerdown', 'touchstart', 'focusin', 'mouseenter'].forEach((type) => this.list.addEventListener(type, () => (this.pausedUntil = Date.now() + 6000), { passive: true }));
    new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting), { threshold: 0.5 }).observe(this.list);
    setInterval(() => this.advance(), delay);
  }

  advance() {
    if (!this.mobile.matches || !this.visible || Date.now() < (this.pausedUntil || 0)) return;
    const item = this.list.querySelector('.scrolling-products__item');
    if (!item) return;
    const end = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 4;
    const step = item.offsetWidth + parseFloat(getComputedStyle(item.parentElement).columnGap || 0);
    this.list.scrollTo({ left: end ? 0 : this.list.scrollLeft + step, behavior: 'smooth' });
  }

  updateProgress() {
    if (!this.bar) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.list;
    const thumb = Math.min(1, clientWidth / scrollWidth);
    const progress = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 0;
    this.bar.style.setProperty('--thumb', `${thumb * 100}%`);
    this.bar.style.setProperty('--offset', `${progress * (1 / thumb - 1) * 100}%`);
  }
}
customElements.define('ds-scrolling-products', ScrollingProducts);

/* Hero: the background video honours reduced motion and has a pause control; the arrow scrolls to the next section. */
class HeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-pause]');
    this.querySelector('[data-scroll-next]')?.addEventListener('click', () => {
      window.scrollTo({ top: window.scrollY + this.getBoundingClientRect().bottom, behavior: 'smooth' });
    });
    if (!this.video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.video.pause();
    this.toggle?.addEventListener('click', () => (this.video.paused ? this.video.play() : this.video.pause()));
    ['play', 'pause'].forEach((type) => this.video.addEventListener(type, () => this.sync()));
    this.sync();
  }

  sync() {
    if (!this.toggle) return;
    const paused = this.video.paused;
    this.toggle.classList.toggle('is-paused', paused);
    this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
  }
}
customElements.define('ds-hero-video', HeroVideo);

/* Header dropdowns (search, mobile menu): close on Escape, on a click outside and after following a link. */
class DetailsModal extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details.querySelector('summary');
    this.details.addEventListener('toggle', () => {
      if (this.details.open) this.querySelector('input[type="search"]')?.focus();
    });
    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !this.details.open) return;
      const focusInside = this.contains(document.activeElement);
      this.details.removeAttribute('open');
      if (focusInside) this.summary.focus();
    });
    document.addEventListener('click', (event) => !this.contains(event.target) && this.details.removeAttribute('open'));
    this.addEventListener('click', (event) => event.target.closest('a[href]') && this.details.removeAttribute('open'));
  }
}
customElements.define('ds-details-modal', DetailsModal);

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.closest('.search-modal')?.querySelector('[data-predictive-search]');
    this.input?.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
    });
  }

  async search(terms) {
    if (!terms) {
      this.innerHTML = '';
      return;
    }
    const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=${this.dataset.limit}&section_id=${this.dataset.sectionId}`;
    const response = await fetch(url);
    if (!response.ok) return;
    const results = htmlFrom(await response.text(), '#PredictiveSearchResults');
    this.replaceChildren(...(results ? [results] : []));
  }
}
customElements.define('ds-predictive-search', PredictiveSearch);

/* Footer "Cookie preferences": shown where Shopify's cookie banner applies; it reopens the banner's preferences. */
class CookiePreferences extends HTMLElement {
  connectedCallback() {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error || !window.Shopify.customerPrivacy?.shouldShowBanner()) return;
      this.closest('li').hidden = false;
    });
    this.querySelector('button').addEventListener('click', () => window.privacyBanner?.showPreferences());
  }
}
customElements.define('ds-cookie-preferences', CookiePreferences);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const response = await fetch(this.dataset.url);
      const next = htmlFrom(await response.text(), 'ds-product-recommendations');
      if (!next?.innerHTML.trim()) return;
      this.innerHTML = next.innerHTML;
      revealWithin(this);
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(this);
  }
}
customElements.define('ds-product-recommendations', ProductRecommendations);

/* Header dropdowns open on hover (pointer devices) and close on outside click. */
document.querySelectorAll('[data-hover-open]').forEach((details) => {
  const item = details.closest('li');
  if (window.matchMedia('(hover: hover)').matches) {
    item.addEventListener('mouseenter', () => details.setAttribute('open', ''));
    item.addEventListener('mouseleave', () => details.removeAttribute('open'));
  }
  document.addEventListener('click', (event) => !details.contains(event.target) && details.removeAttribute('open'));
});

/* Forms that submit on change (sorting, filters). */
document.querySelectorAll('form[data-auto-submit]').forEach((form) => form.addEventListener('change', () => form.submit()));

/* Customer login: toggle password recovery. */
document.querySelectorAll('[data-toggle-recover]').forEach((link) =>
  link.addEventListener('click', (event) => {
    event.preventDefault();
    document.getElementById('RecoverPassword')?.toggleAttribute('hidden');
    document.getElementById('CustomerLogin')?.toggleAttribute('hidden');
  }),
);
if (window.location.hash === '#recover') document.querySelector('[data-toggle-recover]')?.click();

/* Reveal on scroll. Also used for markup injected later (recommendations). */
function revealWithin(root) {
  const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!document.body.classList.contains('reveal-enabled') || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  revealWithin.observer ||= new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' },
  );
  targets.forEach((el) => revealWithin.observer.observe(el));
}
revealWithin(document);
